import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Task, taskService, CreateTaskInput, TaskReadScope } from "../../services/taskService";
import { useSearchParams } from "react-router-dom";
import {
  activeTaskFilterCount,
  clearAccountScopedTaskFilters,
  taskListFiltersFromParams,
  taskListFiltersToParams,
  wantsPortfolioView,
  type TaskListFilters,
} from "@/lib/taskListFilters";
import { useTenant } from "@/contexts/TenantContext";
import { isMergedScope, mergedScopeKey, scopeReachKey } from "@/lib/mergedScope";
import { realtimeFilterFor, scopedChannelName } from "@/lib/realtimeScope";
import {
  accountFilterOptions,
  accountLabelFor,
  accountSortKey,
  buildAccountNames,
} from "@/lib/accountAttribution";
import { AccountCell, AccountFilter } from "@/components/scope/AccountColumn";
import { ScopeAwareEmptyState } from "@/components/scope/ScopeAwareEmptyState";
import { TaskItem } from "./TaskItem";
import { TaskModal } from "./TaskModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PlusIcon,
  FilterIcon,
  Edit,
  Search,
  CheckCircle,
  Circle,
  Check,
  Trash2,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/lib/supabaseClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format, isToday, isYesterday, isTomorrow } from "date-fns";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { usePermissions } from "@/contexts/PermissionContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const formatRelativeDate = (date: Date): string => {
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  if (isTomorrow(date)) return "Tomorrow";
  return format(date, "MMM d, yyyy");
};

export const TaskDetails: React.FC<{
  task: Task;
  // RBAC-3.93: each control renders only when given its handler, so a read-only caller is offered none.
  onEdit?: () => void;
  onDelete?: () => void;
  userId: string | null;
  onComplete?: (id: string) => void;
  onUncomplete?: (id: string) => void;
  onClose?: () => void;
}> = ({
  task,
  onEdit,
  onDelete,
  userId,
  onComplete,
  onUncomplete,
  onClose,
}) => {
  const [assignee, setAssignee] = React.useState<string>("");
  const [assigner, setAssigner] = React.useState<string>("");
  const [projectInfo, setProjectInfo] = React.useState<{
    name: string;
    company: string;
  } | null>(null);
  const canComplete = userId === task.assignee_id || userId === task.user_id;

  React.useEffect(() => {
    if (task.assignee_id) {
      supabase
        .from("users")
        .select("first_name, last_name, email")
        .eq("id", task.assignee_id)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching assignee:", error);
            return;
          }
          if (data) {
            const assigneeName =
              data.first_name && data.last_name
                ? `${data.first_name} ${data.last_name}`
                : data.first_name ||
                  data.last_name ||
                  data.email?.split("@")[0] ||
                  "";
            setAssignee(assigneeName);
          }
        });
    }
    if (task.user_id) {
      supabase
        .from("users")
        .select("first_name, last_name, email")
        .eq("id", task.user_id)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching user:", error);
            return;
          }
          if (data) {
            setAssigner(
              data.first_name && data.last_name
                ? `${data.first_name} ${data.last_name}`
                : data.first_name ||
                    data.last_name ||
                    data.email?.split("@")[0] ||
                    ""
            );
          }
        });
    }
    if (task.project_id) {
      supabase
        .from("projects")
        .select("project_name, company_name")
        .eq("id", task.project_id)
        .single()
        .then(({ data, error }) => {
          if (error) {
            console.error("Error fetching project:", error);
            return;
          }
          if (data) {
            setProjectInfo({
              name: data.project_name || "Unnamed Project",
              company: data.company_name || "No company name",
            });
          }
        });
    }
  }, [task.assignee_id, task.user_id, task.project_id]);

  const handleProjectClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (task.project_id) {
      // Navigate to pipeline view with project modal open
      window.location.href = `/app/pipeline?projectId=${task.project_id}&modal=1`;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold break-words leading-tight flex items-center gap-2 text-foreground">
            {task.title}
            {canComplete && onComplete && task.status !== "completed" && (
              <button
                onClick={() => {
                  onComplete(task.id);
                  onClose && onClose();
                }}
                className="ml-2 focus:outline-none group text-primary"
                title="Mark as complete"
              >
                <CheckCircle
                  size={24}
                  className="text-primary hover:scale-110 transition-transform"
                />
              </button>
            )}
          </h2>
          <div className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">Assigned to:</span>{" "}
            {assignee || "Unassigned"}
          </div>
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">Assigned by:</span>{" "}
            {assigner || "Unknown"}
          </div>
          {projectInfo && (
            <div className="text-sm text-muted-foreground mt-1">
              <span className="font-medium">Project:</span>{" "}
              <button
                onClick={handleProjectClick}
                className="text-primary hover:underline focus:outline-none"
              >
                {projectInfo.name}
                <span className="text-muted-foreground ml-1">
                  ({projectInfo.company})
                </span>
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {onEdit && (
            <button
              className="p-2 rounded hover:bg-accent text-primary transition-colors duration-200"
              aria-label="Edit task"
              onClick={onEdit}
            >
              <Edit className="w-5 h-5" />
            </button>
          )}
          {onDelete && userId === task.user_id && (
            <button
              className="p-2 rounded hover:bg-destructive/10 text-destructive"
              aria-label="Delete task"
              onClick={() => {
                if (
                  window.confirm("Are you sure you want to delete this task?")
                ) {
                  onDelete();
                }
              }}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      <hr className="my-2" />
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span
          className={`px-2 py-1 rounded-full font-medium ${
            task.priority === "high"
              ? "bg-destructive/10 text-destructive"
              : task.priority === "medium"
                ? "bg-primary/10 text-primary"
                : "bg-success/10 text-success"
          }`}
        >
          {task.priority}
        </span>
        {task.due_date && (
          <span className="flex items-center gap-1 text-foreground">
            Due:{" "}
            <span className="font-medium">
              {formatRelativeDate(new Date(task.due_date))}
            </span>
          </span>
        )}
        <span className="text-foreground">
          Status:{" "}
          <span className="font-medium">
            {task.status === "todo"
              ? "Incomplete"
              : task.status.replace("_", " ")}
          </span>
        </span>
        {task.status === "completed" && task.completed_at && (
          <span className="flex items-center gap-1 text-foreground">
            Completed:{" "}
            <span className="font-medium">
              {formatRelativeDate(new Date(task.completed_at))}
            </span>
          </span>
        )}
      </div>
      {task.description && (
        <div className="max-w-none border-t pt-4 text-foreground [&_p]:text-foreground [&_strong]:text-foreground [&_em]:text-foreground [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground [&_li]:text-foreground">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {task.description}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

// Add a helper to fetch assignee names for all tasks
const useAssigneeNames = (tasks: Task[]) => {
  const [assignees, setAssignees] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    const ids = Array.from(
      new Set(tasks.map((t) => t.assignee_id).filter(Boolean))
    );
    if (ids.length === 0) return;
    supabase
      .from("users")
      .select("id, first_name, last_name, email")
      .in("id", ids)
      .then(({ data }) => {
        if (data) {
          const map: Record<string, string> = {};
          data.forEach((u: any) => {
            map[u.id] =
              u.first_name && u.last_name
                ? `${u.first_name} ${u.last_name}`
                : u.first_name || u.last_name || u.email?.split("@")[0] || "";
          });
          setAssignees(map);
        }
      });
  }, [tasks]);
  return assignees;
};

const GoldArrow: React.FC<{ direction: "asc" | "desc" }> = ({ direction }) => (
  <span className="text-primary font-bold text-lg ml-1 inline-block align-middle">
    {direction === "asc" ? "⮝" : "⮟"}
  </span>
);

interface TaskListProps {
  initialTaskId?: string | null;
}

export const TaskList: React.FC<TaskListProps> = ({ initialTaskId }) => {
  const navigate = useNavigate();
  const { roleInfo } = usePermissions();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Add CSS for high z-index task modals to appear above project modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .task-modal-high-z {
        z-index: 250 !important;
      }
      [data-radix-portal] [data-state="open"] .task-modal-high-z {
        z-index: 250 !important;
      }
      [data-radix-portal] [data-radix-dialog-overlay] {
        z-index: 250 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sortBy, setSortBy] = useState<
    "created" | "due" | "priority" | "title" | "assignee" | "completed_at" | "account"
  >("due");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchQuery, setSearchQuery] = useState("");
  // SUB-5.6 · D65. A drill-down from M3 asks for the portfolio view by name.
  // ⚠️ Without this the link would filter the caller's OWN list, which opens on
  // "Assigned to Me", and show almost nothing.
  const [activeTab, setActiveTab] = useState<
    "assigned-to-me" | "assigned-by-me" | "assigned-to-projects"
  >(() =>
    wantsPortfolioView(new URLSearchParams(window.location.search))
      ? "assigned-to-projects"
      : "assigned-to-me",
  );
  const [userId, setUserId] = useState<string | null>(null);
  const channelRef = useRef<any>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editing, setEditing] = useState(false);
  const assignees = useAssigneeNames(tasks);
  const optimisticUpdateInProgress = useRef(false);
  const { toast } = useToast();
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [assignmentDialogName, setAssignmentDialogName] = useState("");
  const [assignmentDialogCountdown, setAssignmentDialogCountdown] = useState(3);
  const [projectNames, setProjectNames] = useState<
    Record<string, { name: string; company: string }>
  >({});
  const hasManuallyClosedRef = useRef(false);

  // SUB-4.2 · FR34, FR35. Tasks is one of the three surfaces merged scope covers.
  // SUB-4.10 · D-6. `scopeStatus` decides whether "No tasks found" is a claim
  // this list is entitled to make.
  const { mergedScope, userTenants, scopeStatus } = useTenant();
  const inMergedScope = isMergedScope(mergedScope);
  const [accountFilter, setAccountFilter] = useState<string | null>(null);

  /**
   * SUB-5.6 · FR47. The drill-down's filter state, and the URL is where it lives.
   *
   * ⚠️ **THE SAME SHAPE AS THE PROJECTS LIST, NOT A SECOND IDIOM.** Derived from
   * `searchParams` rather than copied into state, so a second drill-down clicked
   * while already on this page applies rather than only changing the address bar.
   * That is the trap SUB-5.5 found on `/app/projects`, where the state was seeded
   * in a `useState` initialiser that runs once.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTaskFilters = React.useMemo<TaskListFilters>(
    () => taskListFiltersFromParams(searchParams),
    [searchParams],
  );
  const taskFilterCount = activeTaskFilterCount(urlTaskFilters);
  const taskFilterKey = taskListFiltersToParams(urlTaskFilters).toString();

  /**
   * ⚠️ **THE TAB IS FILTER STATE TOO, AND THE INITIALISER ABOVE RUNS ONCE.**
   *
   * The block twelve lines up explains why `urlTaskFilters` is derived from
   * `searchParams` rather than copied into state, and names the `useState`
   * initialiser as the trap. `activeTab` was seeded by exactly that initialiser,
   * so the warning and the bug shipped in the same component.
   *
   * React Router does not remount a route when only the query string changes.
   * So a second M3 drill-down clicked while already on `/app/tasks` produced a
   * new `taskFilterKey`, re-ran `loadTasks`, and left `activeTab` on the
   * personal list, whose readers take no `urlTaskFilters` at all and whose tab
   * renders no "filters applied" banner. The reader saw their own tasks with no
   * sign a filter had been asked for.
   */
  useEffect(() => {
    if (wantsPortfolioView(searchParams)) setActiveTab('assigned-to-projects');
  }, [searchParams]);

  const clearTaskFilters = React.useCallback(() => {
    // ⚠️ Params this module does not own are carried through. `?taskId=` predates
    // merged scope and something else owns it; dropping it here would break a
    // deep link that has nothing to do with filtering.
    setSearchParams(
      (current) => {
        const next = new URLSearchParams();
        const owned = new Set(['view', 'account', 'assignee', 'open']);
        current.forEach((value, key) => {
          if (!owned.has(key)) next.set(key, value);
        });
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const accountNames = React.useMemo(
    () => buildAccountNames(userTenants),
    [userTenants],
  );
  const accountOptions = React.useMemo(
    () => accountFilterOptions(mergedScope.tenantIds, accountNames),
    [mergedScope.tenantIds, accountNames],
  );
  // What the rows were fetched under. The 30-second cache in taskService is keyed
  // on this, because in merged scope the anchor is the parent account and an
  // anchor-keyed cache cannot tell a merged read from a single-account one.
  const scopeCacheKey = `${mergedScopeKey(mergedScope)}|${accountFilter ?? 'all'}`;
  // SUB-4.7 · AC1. The REACH, not the status object and not `scopeCacheKey`.
  // The realtime subscription must be rebuilt when the accounts change and NOT
  // when the account filter does: the filter is a client-side narrowing of rows
  // already fetched, and `refreshMergedScope` returns a fresh payload once a
  // minute, so keying the channel on either would drop and rejoin it for
  // nothing.
  const scopeKey = scopeReachKey(mergedScope);
  const taskReadScope: TaskReadScope | undefined = inMergedScope
    ? { merged: true, accountTenantId: accountFilter, cacheKey: scopeCacheKey }
    : undefined;

  // ⚠️ A narrowed account that leaves the scope must not stay applied. If the
  // grant is revoked or the sub-account detached, the id is no longer in the
  // resolver's answer, and a stale `.eq` on it would render an empty list that
  // looks like "this account has no tasks" rather than "you left that account".
  useEffect(() => {
    if (accountFilter && !mergedScope.tenantIds.includes(accountFilter)) {
      setAccountFilter(null);
    }
  }, [accountFilter, mergedScope.tenantIds]);

  // SUB-5.6. The same rule the Projects list applies: a person named from one
  // account's users may not be nameable in the next scope, so the filter is
  // dropped rather than left applied as an invisible predicate.
  // ⚠️ `unassigned` survives, because it names no person.
  // ⚠️ **NOT ON MOUNT.** A `useEffect` with a dep list still runs on the first
  // commit, and `clearAccountScopedTaskFilters` drops a named assignee
  // unconditionally, because it compares nothing: it cannot tell a scope CHANGE
  // from a first render. So a drill-down landing at `?assignee=<uuid>&open=1`
  // had its assignee deleted before a row was drawn, and the reader got every
  // open task in the subtree under a banner still reading "1 filter applied"
  // (the surviving `openOnly`). The URL is the caller's instruction on mount;
  // only a LATER scope change invalidates it.
  const scopeClearArmed = useRef(false);
  useEffect(() => {
    if (!scopeClearArmed.current) {
      scopeClearArmed.current = true;
      return;
    }
    const cleared = clearAccountScopedTaskFilters(urlTaskFilters);
    if (cleared === urlTaskFilters) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('assignee');
        return next;
      },
      { replace: true },
    );
    // Keyed on the scope alone: depending on the filters here would re-run the
    // clear on every filter edit, which is not what a scope change means.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Check if user has privileged role for project tasks tab
  //
  // ⚠️ **`Agency Admin` IS HERE BECAUSE `tasks_select` PUTS IT THERE.** Read from
  // `pg_policies` 2026-08-17, the live policy admits Superadmin, Admin, Agency
  // Admin, Internal PM and Internal PM Manager, resolved per row. SUB-4.19 added
  // that role deliberately when it closed F-74. This literal list had four of
  // the five, and the roll-up dashboard is an Agency Admin surface (SUB-5.0 §1),
  // so that persona read a populated M3, clicked it, and landed on a tab whose
  // trigger and content are both gated off: an empty pane with a fetch running
  // behind it.
  //
  // ⚠️ This gate reveals a TAB. It is not the access control; `tasks_select` is,
  // and it still bounds every row. A UI gate narrower than its policy does not
  // add safety, it just hides a surface from someone entitled to it.
  const hasProjectTasksAccess = roleInfo && [
    'Superadmin', 'Admin', 'Agency Admin', 'Internal PM', 'Internal PM Manager'
  ].includes(roleInfo.name);

  useEffect(() => {
    // Get current user ID for filtering
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
    });
  }, []);

  /**
   * Subscribe to changes on the tasks table.
   *
   * SUB-4.7 · FR74, AC1. `tasks` is scope-governed: `tasks_select` resolves
   * through `merged_scope_tenant_ids()` and `realtime.apply_rls` re-evaluates it
   * per event with this subscriber's own claims, so one channel spans the
   * subtree in merged scope and one account outside it.
   *
   * ⚠️ THE HANDLER REFETCHES AND NEVER READS THE PAYLOAD, which is what makes
   * this safe for DELETE. Deletes reach every subscriber of `public:tasks`
   * without an RLS check (F-44); `loadTasks()` goes back through the policy.
   */
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(scopedChannelName(`tasks_changes_all_${userId}`, mergedScope))
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          // undefined for a scope-governed table. A client filter runs BEFORE
          // the policy, so adding one here would silently drop every
          // sub-account task from a merged list.
          filter: realtimeFilterFor("tasks", mergedScope.singleAccountTenantId),
        },
        () => {
          loadTasks();
        }
      )
      .subscribe();
    channelRef.current = channel;
    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [userId, activeTab, scopeKey]);

  useEffect(() => {
    // Active tab changed, or the scope the rows were fetched under did.
    // SUB-4.2: entering or leaving merged scope, and narrowing to one account,
    // both change WHICH rows the server may return, so both must refetch. The
    // key carries the account filter, so this covers FR35 as well.
    loadTasks();
    // ⚠️ `taskFilterKey` is a dependency because a drill-down changes WHICH rows
    // the server returns without touching the tab or the scope. Omitting it
    // leaves the previous person's list on screen under the new URL, which is
    // the silently-not-applied pre-filter D35 is about.
  }, [activeTab, scopeCacheKey, taskFilterKey]);

  useEffect(() => {
    if (showAssignmentDialog) {
      setAssignmentDialogCountdown(3);
      const interval = setInterval(() => {
        setAssignmentDialogCountdown((prev) => {
          if (prev <= 1) {
            setShowAssignmentDialog(false);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [showAssignmentDialog]);

  // Auto-open task if initialTaskId is provided
  useEffect(() => {
    if (
      initialTaskId &&
      tasks.length > 0 &&
      !selectedTask &&
      !hasManuallyClosedRef.current
    ) {
      const taskToOpen = tasks.find((task) => task.id === initialTaskId);
      if (taskToOpen) {
        setSelectedTask(taskToOpen);
      } else {
      }
    }
  }, [initialTaskId, tasks, selectedTask]);

  // Reset the manually closed flag when initialTaskId changes
  useEffect(() => {
    hasManuallyClosedRef.current = false;
  }, [initialTaskId]);

  // Handle closing task modal and removing URL parameter
  const handleCloseTaskModal = () => {
    hasManuallyClosedRef.current = true; // Prevent auto-reopen
    setSelectedTask(null);
    setEditing(false);

    // Remove taskId from URL if present
    if (initialTaskId) {
      navigate("/app/tasks", { replace: true });
    }
  };

  const fetchProjectNames = async (projectIds: string[]) => {
    if (projectIds.length === 0) return;

    try {
      const { data: projects, error } = await supabase
        .from("projects")
        .select("id, project_name, company_name")
        .in("id", projectIds);

      if (error) throw error;

      const projectMap = (projects || []).reduce(
        (acc, project) => ({
          ...acc,
          [project.id]: {
            name: project.project_name || "Unnamed Project",
            company: project.company_name || "No company name",
          },
        }),
        {}
      );

      setProjectNames((prev) => ({ ...prev, ...projectMap }));
    } catch (error) {
      console.error("Error fetching project names:", error);
    }
  };

  const loadTasks = async () => {
    setIsLoading(true);
    setError(null); // Clear any previous errors
    try {
      let tasks;
      if (activeTab === "assigned-to-me") {
        tasks = await taskService.getAssignedToMeTasks(taskReadScope);
      } else if (activeTab === "assigned-by-me") {
        tasks = await taskService.getAssignedByMeTasks(taskReadScope);
      } else if (activeTab === "assigned-to-projects") {
        /**
         * SUB-5.6 · D66. Every task in scope, not only those attached to a
         * project.
         *
         * ⚠️ **THIS TAB'S MEANING WIDENED, DELIBERATELY, AND HERE IS THE SIZE OF
         * IT.** `getProjectTasks` required `project_id IS NOT NULL`. Measured
         * 2026-08-16: 130 open tasks live, of which it reached 128. M3 on the
         * roll-up counts all 130, so a drill-down onto this tab would have landed
         * on a list two rows short of the figure that opened it, which is the
         * tile-disagrees-with-its-list failure SUB-5.5 spent a story avoiding.
         *
         * The bound is unchanged: `tasks_select` still resolves
         * `merged_scope_tenant_ids()` and still requires an Admin, Superadmin,
         * Internal PM or Internal PM Manager role, and this tab is already gated
         * on exactly those four. Six rows became visible to people who could
         * already see the other 216 in the same accounts.
         */
        tasks = await taskService.getPortfolioTasks(taskReadScope, urlTaskFilters);
      }
      const safeTasks = tasks || [];
      setTasks(safeTasks);

      // Fetch project names for tasks with project_id
      const projectIds = safeTasks
        .filter((task) => task.project_id)
        .map((task) => task.project_id)
        .filter((id): id is string => id !== undefined);

      if (projectIds.length > 0) {
        try {
          await fetchProjectNames(projectIds);
        } catch (projectErr) {
          // Don't fail the whole task loading if project names fail
          console.warn("Failed to load project names:", projectErr);
        }
      }
    } catch (err) {
      setError("Failed to load tasks");
      console.error("Error loading tasks:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTask = async (task: CreateTaskInput) => {
    try {
      const newTask = await taskService.createTask(task);
      setTasks([newTask, ...tasks]);
      setShowTaskForm(false);
      // Show assignment message if assigned to someone else (or if userId is missing)
      if (newTask.assignee_id && (!userId || newTask.assignee_id !== userId)) {
        let assigneeName = "";
        const { data } = await supabase
          .from("users")
          .select("first_name, last_name, email")
          .eq("id", newTask.assignee_id)
          .single();
        if (data) {
          assigneeName =
            data.first_name && data.last_name
              ? `${data.first_name} ${data.last_name}`
              : data.first_name ||
                data.last_name ||
                data.email?.split("@")[0] ||
                "";
        }
        setAssignmentDialogName(assigneeName || "user");
        setShowAssignmentDialog(true);
      }
    } catch (err) {
      setError("Failed to create task");
      console.error("Error creating task:", err);
    }
  };

  const handleUpdateTask = async (id: string, updates: Partial<Task>) => {
    try {
      const updatedTask = await taskService.updateTask(id, updates);
      setTasks(tasks.map((task) => (task.id === id ? updatedTask : task)));
    } catch (err) {
      setError("Failed to update task");
      console.error("Error updating task:", err);
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      await taskService.deleteTask(id);
      setTasks(tasks.filter((task) => task.id !== id));
    } catch (err) {
      setError("Failed to delete task");
      console.error("Error deleting task:", err);
    }
  };

  const handleCompleteTask = async (id: string) => {
    // Optimistic update
    const prevTasks = [...tasks];
    optimisticUpdateInProgress.current = true;
    setTasks(
      tasks.map((task) =>
        task.id === id
          ? {
              ...task,
              status: "completed",
              completed_at: new Date().toISOString(),
            }
          : task
      )
    );
    try {
      const completedTask = await taskService.completeTask(id);
      setTasks((tasks) =>
        tasks.map((task) => (task.id === id ? completedTask : task))
      );
    } catch (err) {
      setError("Failed to complete task");
      setTasks(prevTasks); // revert
      console.error("Error completing task:", err);
    }
  };

  const handleUncompleteTask = async (id: string) => {
    // Optimistic update
    const prevTasks = [...tasks];
    optimisticUpdateInProgress.current = true;
    setTasks(
      tasks.map((task) =>
        task.id === id
          ? { ...task, status: "todo", completed_at: undefined }
          : task
      )
    );
    try {
      const uncompletedTask = await taskService.uncompleteTask(id);
      setTasks((tasks) =>
        tasks.map((task) => (task.id === id ? uncompletedTask : task))
      );
    } catch (err) {
      setError("Failed to uncomplete task");
      setTasks(prevTasks); // revert
      console.error("Error uncompleting task:", err);
    }
  };

  const handleSort = (
    column:
      | "created"
      | "due"
      | "priority"
      | "title"
      | "assignee"
      | "completed_at"
      // SUB-4.2 · FR34, design B1.
      | "account"
  ) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const filteredTasks = tasks
    .filter((task) => {
      if (showCompleted) return true;
      return task.status !== "completed";
    })
    .filter((task) => {
      // For "assigned-to-projects" tab, only show tasks with valid projects
      if (activeTab === "assigned-to-projects") {
        return task.project_id && projectNames[task.project_id];
      }
      return true;
    })
    .filter(
      (task) =>
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      switch (sortBy) {
        case "due":
          return (
            direction *
            (new Date(a.due_date || "").getTime() -
              new Date(b.due_date || "").getTime())
          );
        case "priority": {
          const priorityOrder = { urgent: -1, high: 0, medium: 1, low: 2 };
          return (
            direction * (priorityOrder[a.priority] - priorityOrder[b.priority])
          );
        }
        case "title":
          return direction * a.title.localeCompare(b.title);
        case "assignee": {
          const aName = assignees[a.assignee_id ?? ""] || "";
          const bName = assignees[b.assignee_id ?? ""] || "";
          return direction * aName.localeCompare(bName);
        }
        // SUB-4.2 · FR34, design B1. Sorting by account is why the attribution is
        // a column rather than a badge on the title.
        case "account":
          return (
            direction *
            accountSortKey(a.tenant_id, accountNames).localeCompare(
              accountSortKey(b.tenant_id, accountNames),
            )
          );
        case "completed_at":
          return (
            direction *
            (new Date(a.completed_at || 0).getTime() -
              new Date(b.completed_at || 0).getTime())
          );
        case "created":
        default:
          return (
            direction *
            (new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime())
          );
      }
    });

  return (
    <div className="space-y-4 relative">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Tasks</h2>
        <Button onClick={() => setShowTaskForm(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          New Task
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as "assigned-to-me" | "assigned-by-me" | "assigned-to-projects");
        }}
        className="w-full"
      >
        <TabsList className={`flex w-full gap-0 rounded-lg shadow-sm border border-muted bg-transparent p-0 mb-4`}>
          <TabsTrigger
            value="assigned-to-me"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
          >
            Assigned to Me
          </TabsTrigger>
          <TabsTrigger
            value="assigned-by-me"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
          >
            Assigned by Me
          </TabsTrigger>
          {hasProjectTasksAccess && (
            <TabsTrigger
              value="assigned-to-projects"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-2 transition-colors data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
            >
              All Tasks
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="assigned-to-me" className="space-y-2">
          <div className="flex justify-between items-center gap-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-grow md:flex-grow-0 w-full md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search tasks..."
                  className="pl-8 w-full h-10 border-border bg-muted focus:bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {/* SUB-4.2 · FR35. Offered only in merged scope: in single scope every
                  row is the same account, so the control would have one meaningful
                  setting. The chosen id narrows the QUERY, never the fetched rows. */}
              {inMergedScope && (
                <AccountFilter
                  options={accountOptions}
                  value={accountFilter}
                  onChange={setAccountFilter}
                />
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-completed"
                checked={showCompleted}
                onCheckedChange={setShowCompleted}
              />
              <Label
                htmlFor="show-completed"
                className="text-sm text-foreground truncate"
              >
                Show completed tasks
              </Label>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-red-400 text-destructive px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="border rounded-lg shadow-sm">
              <Table className="w-full" style={{tableLayout: 'fixed'}}>
                <TableHeader className="bg-muted">
                  <TableRow>
                    {/* SUB-4.2 · FR34, design B1. Which account this row belongs to, first
                        column and sortable. Rendered only in merged scope: in single scope
                        every row is the same account and the column would be noise. */}
                    {inMergedScope && (
                      <TableHead
                        className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '14%'}}
                        onClick={() => handleSort("account")}
                      >
                        Account{" "}
                        {sortBy === "account" && (
                          <GoldArrow direction={sortDirection} />
                        )}
                      </TableHead>
                    )}
                    <TableHead className="font-semibold text-muted-foreground" style={{width: '8%'}}>
                      Status
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '30%'}}
                      onClick={() => handleSort("title")}
                    >
                      Title{" "}
                      {sortBy === "title" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground" style={{width: '25%'}}>
                      Project
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '15%'}}
                      onClick={() => handleSort("assignee")}
                    >
                      Assignee{" "}
                      {sortBy === "assignee" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                      onClick={() => handleSort("due")}
                    >
                      Due Date{" "}
                      {sortBy === "due" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="w-[8%] font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200"
                      onClick={() => handleSort("priority")}
                    >
                      Priority{" "}
                      {sortBy === "priority" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                      onClick={() => handleSort("completed_at")}
                    >
                      Completed At{" "}
                      {sortBy === "completed_at" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-card">
                {filteredTasks.map((task) => (
                  <TableRow
                    key={task.id}
                    className={`hover:bg-muted cursor-pointer transition-colors duration-200 ${task.status === "completed" ? "opacity-70" : ""}`}
                    onClick={(e) => {
                      if (
                        (e.target as HTMLElement).closest(
                          'button, input[type="checkbox"]'
                        )
                      )
                        return;
                      setSelectedTask(task);
                    }}
                  >
                    {inMergedScope && (
                      <TableCell className="truncate text-sm" style={{maxWidth: '0', width: '14%'}}>
                        <AccountCell label={accountLabelFor(task.tenant_id, accountNames)} />
                      </TableCell>
                    )}
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (task.status === "completed") {
                            handleUncompleteTask(task.id);
                          } else {
                            handleCompleteTask(task.id);
                          }
                        }}
                        className="focus:outline-none group text-primary"
                        title={
                          task.status === "completed"
                            ? "Mark as incomplete"
                            : "Mark as complete"
                        }
                      >
                        {task.status === "completed" ? (
                          <span className="relative inline-block w-6 h-6 align-middle">
                            <Circle className="w-6 h-6 fill-primary stroke-primary" />
                            <Check
                              className="w-4 h-4 text-primary-foreground absolute left-1 top-1"
                              strokeWidth={3}
                            />
                          </span>
                        ) : (
                          <span className="relative inline-block w-6 h-6 align-middle">
                            <Circle className="w-6 h-6 transition-colors fill-transparent group-hover:fill-primary stroke-primary" />
                            <Check
                              className="w-4 h-4 text-primary-foreground absolute left-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              strokeWidth={3}
                            />
                          </span>
                        )}
                      </button>
                    </TableCell>
                    <TableCell
                      className={`font-medium text-sm text-foreground truncate ${task.status === "completed" ? "line-through" : ""}`}
                      title={task.title}
                      style={{maxWidth: '0', width: '30%'}}
                    >
                      {task.title}
                    </TableCell>
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      {task.project_id && projectNames[task.project_id] ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-sm text-foreground truncate" title={projectNames[task.project_id].name}>
                            {projectNames[task.project_id].name}
                          </span>
                          <span className="text-xs text-muted-foreground truncate" title={projectNames[task.project_id].company}>
                            {projectNames[task.project_id].company}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          No project
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate" title={assignees[task.assignee_id ?? ""] || ""} style={{maxWidth: '0', width: '15%'}}>
                      {assignees[task.assignee_id ?? ""] || ""}
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate" title={task.due_date ? formatRelativeDate(new Date(task.due_date)) : ""} style={{maxWidth: '0', width: '12%'}}>
                      {task.due_date
                        ? formatRelativeDate(new Date(task.due_date))
                        : ""}
                    </TableCell>
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          task.priority === "high"
                            ? "bg-destructive/10 text-destructive"
                            : task.priority === "medium"
                              ? "bg-primary/10 text-primary"
                              : "bg-success/10 text-success"
                        }`}
                      >
                        {task.priority}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate">
                      {task.status === "completed" && task.completed_at
                        ? formatRelativeDate(new Date(task.completed_at))
                        : ""}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTasks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={inMergedScope ? 8 : 7}
                      className="text-center text-muted-foreground py-8"
                    >
                      {/* SUB-4.10 · D-6. NFR8's second sentence. */}
                      <ScopeAwareEmptyState scopeStatus={scopeStatus}>
                        No tasks found
                      </ScopeAwareEmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="assigned-by-me" className="space-y-2">
          <div className="flex justify-between items-center gap-4">
            <div className="flex gap-4 items-center">
              <div className="relative flex-grow md:flex-grow-0 w-full md:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search tasks..."
                  className="pl-8 w-full h-10 border-border bg-muted focus:bg-background"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {/* SUB-4.2 · FR35. Offered only in merged scope: in single scope every
                  row is the same account, so the control would have one meaningful
                  setting. The chosen id narrows the QUERY, never the fetched rows. */}
              {inMergedScope && (
                <AccountFilter
                  options={accountOptions}
                  value={accountFilter}
                  onChange={setAccountFilter}
                />
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="show-completed-by-me"
                checked={showCompleted}
                onCheckedChange={setShowCompleted}
              />
              <Label
                htmlFor="show-completed-by-me"
                className="text-sm text-foreground truncate"
              >
                Show completed tasks
              </Label>
            </div>
          </div>

          <div className="border rounded-lg shadow-sm">
              <Table className="w-full" style={{tableLayout: 'fixed'}}>
                <TableHeader className="bg-muted">
                <TableRow>
                  {/* SUB-4.2 · FR34, design B1. Which account this row belongs to, first
                      column and sortable. Rendered only in merged scope: in single scope
                      every row is the same account and the column would be noise. */}
                  {inMergedScope && (
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '14%'}}
                      onClick={() => handleSort("account")}
                    >
                      Account{" "}
                      {sortBy === "account" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                  )}
                  <TableHead className="font-semibold text-muted-foreground" style={{width: '8%'}}>
                    Status
                  </TableHead>
                  <TableHead
                    className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '30%'}}
                    onClick={() => handleSort("title")}
                  >
                    Title{" "}
                    {sortBy === "title" && (
                      <GoldArrow direction={sortDirection} />
                    )}
                  </TableHead>
                  <TableHead className="font-semibold text-muted-foreground" style={{width: '25%'}}>
                    Project
                  </TableHead>
                  <TableHead
                    className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '15%'}}
                    onClick={() => handleSort("assignee")}
                  >
                    Assignee{" "}
                    {sortBy === "assignee" && (
                      <GoldArrow direction={sortDirection} />
                    )}
                  </TableHead>
                  <TableHead
                    className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                    onClick={() => handleSort("due")}
                  >
                    Due Date{" "}
                    {sortBy === "due" && (
                      <GoldArrow direction={sortDirection} />
                    )}
                  </TableHead>
                  <TableHead
                    className="w-[8%] font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200"
                    onClick={() => handleSort("priority")}
                  >
                    Priority{" "}
                    {sortBy === "priority" && (
                      <GoldArrow direction={sortDirection} />
                    )}
                  </TableHead>
                  <TableHead
                    className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                    onClick={() => handleSort("completed_at")}
                  >
                    Completed At{" "}
                    {sortBy === "completed_at" && (
                      <GoldArrow direction={sortDirection} />
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-card">
                {filteredTasks.map((task) => (
                  <TableRow
                    key={task.id}
                    className={`hover:bg-muted cursor-pointer transition-colors duration-200 ${task.status === "completed" ? "opacity-70" : ""}`}
                    onClick={(e) => {
                      if (
                        (e.target as HTMLElement).closest(
                          'button, input[type="checkbox"]'
                        )
                      )
                        return;
                      setSelectedTask(task);
                    }}
                  >
                    {inMergedScope && (
                      <TableCell className="truncate text-sm" style={{maxWidth: '0', width: '14%'}}>
                        <AccountCell label={accountLabelFor(task.tenant_id, accountNames)} />
                      </TableCell>
                    )}
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (task.status === "completed") {
                            handleUncompleteTask(task.id);
                          } else {
                            handleCompleteTask(task.id);
                          }
                        }}
                        className="focus:outline-none group text-primary"
                        title={
                          task.status === "completed"
                            ? "Mark as incomplete"
                            : "Mark as complete"
                        }
                      >
                        {task.status === "completed" ? (
                          <span className="relative inline-block w-6 h-6 align-middle">
                            <Circle className="w-6 h-6 fill-primary stroke-primary" />
                            <Check
                              className="w-4 h-4 text-primary-foreground absolute left-1 top-1"
                              strokeWidth={3}
                            />
                          </span>
                        ) : (
                          <span className="relative inline-block w-6 h-6 align-middle">
                            <Circle className="w-6 h-6 transition-colors fill-transparent group-hover:fill-primary stroke-primary" />
                            <Check
                              className="w-4 h-4 text-primary-foreground absolute left-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              strokeWidth={3}
                            />
                          </span>
                        )}
                      </button>
                    </TableCell>
                    <TableCell
                      className={`font-medium text-sm text-foreground truncate ${task.status === "completed" ? "line-through" : ""}`}
                      title={task.title}
                      style={{maxWidth: '0', width: '30%'}}
                    >
                      {task.title}
                    </TableCell>
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      {task.project_id && projectNames[task.project_id] ? (
                        <div className="flex flex-col">
                          <span className="font-medium text-sm text-foreground truncate" title={projectNames[task.project_id].name}>
                            {projectNames[task.project_id].name}
                          </span>
                          <span className="text-xs text-muted-foreground truncate" title={projectNames[task.project_id].company}>
                            {projectNames[task.project_id].company}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          No project
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate" title={assignees[task.assignee_id ?? ""] || ""} style={{maxWidth: '0', width: '15%'}}>
                      {assignees[task.assignee_id ?? ""] || ""}
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate" title={task.due_date ? formatRelativeDate(new Date(task.due_date)) : ""} style={{maxWidth: '0', width: '12%'}}>
                      {task.due_date
                        ? formatRelativeDate(new Date(task.due_date))
                        : ""}
                    </TableCell>
                    <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          task.priority === "high"
                            ? "bg-destructive/10 text-destructive"
                            : task.priority === "medium"
                              ? "bg-primary/10 text-primary"
                              : "bg-success/10 text-success"
                        }`}
                      >
                        {task.priority}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-foreground truncate">
                      {task.status === "completed" && task.completed_at
                        ? formatRelativeDate(new Date(task.completed_at))
                        : ""}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTasks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={inMergedScope ? 8 : 7}
                      className="text-center text-muted-foreground py-8"
                    >
                      {/* SUB-4.10 · D-6. NFR8's second sentence. */}
                      <ScopeAwareEmptyState scopeStatus={scopeStatus}>
                        No tasks found
                      </ScopeAwareEmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        {hasProjectTasksAccess && (
          <TabsContent value="assigned-to-projects" className="space-y-2">
            {/* SUB-5.6 · FR47. ⚠️ A FILTER A USER CANNOT SEE IS A FILTER THEY
                CANNOT CLEAR. A drill-down from the roll-up arrives with an
                assignee or an account applied and nothing else on this tab says
                so, which would read as "this person has no work" rather than as
                "you are filtered". Same rule and same wording as the Projects
                list. */}
            {taskFilterCount > 0 && (
              <p className="text-sm text-muted-foreground">
                {taskFilterCount === 1 ? '1 filter applied' : `${taskFilterCount} filters applied`}
                {urlTaskFilters.openOnly && ' · open work only'}
                <button
                  className="ml-2 text-primary text-sm hover:underline"
                  onClick={clearTaskFilters}
                >
                  Clear filters
                </button>
              </p>
            )}
            <div className="flex justify-between items-center gap-4">
              <div className="flex gap-4 items-center">
                <div className="relative flex-grow md:flex-grow-0 w-full md:w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search tasks..."
                    className="pl-8 w-full h-10 border-border bg-muted focus:bg-background"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                {/* SUB-4.2 · FR35. Offered only in merged scope: in single scope every
                    row is the same account, so the control would have one meaningful
                    setting. The chosen id narrows the QUERY, never the fetched rows. */}
                {inMergedScope && (
                  <AccountFilter
                    options={accountOptions}
                    value={accountFilter}
                    onChange={setAccountFilter}
                  />
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-completed-projects"
                  checked={showCompleted}
                  onCheckedChange={setShowCompleted}
                />
                <Label
                  htmlFor="show-completed-projects"
                  className="text-sm text-foreground truncate"
                >
                  Show completed tasks
                </Label>
              </div>
            </div>

            <div className="border rounded-lg shadow-sm">
                <Table className="w-full" style={{tableLayout: 'fixed'}}>
                  <TableHeader className="bg-muted">
                    <TableRow>
                      {/* SUB-4.2 · FR34, design B1. Which account this row belongs to, first
                          column and sortable. Rendered only in merged scope: in single scope
                          every row is the same account and the column would be noise. */}
                      {inMergedScope && (
                        <TableHead
                          className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '14%'}}
                          onClick={() => handleSort("account")}
                        >
                          Account{" "}
                          {sortBy === "account" && (
                            <GoldArrow direction={sortDirection} />
                          )}
                        </TableHead>
                      )}
                      <TableHead className="font-semibold text-muted-foreground" style={{width: '8%'}}>
                        Status
                      </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '30%'}}
                      onClick={() => handleSort("title")}
                    >
                      Title{" "}
                      {sortBy === "title" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead className="font-semibold text-muted-foreground" style={{width: '25%'}}>
                      Project
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '15%'}}
                      onClick={() => handleSort("assignee")}
                    >
                      Assignee{" "}
                      {sortBy === "assignee" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                      onClick={() => handleSort("due")}
                    >
                      Due Date{" "}
                      {sortBy === "due" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="w-[8%] font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200"
                      onClick={() => handleSort("priority")}
                    >
                      Priority{" "}
                      {sortBy === "priority" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                    <TableHead
                      className="font-semibold text-muted-foreground cursor-pointer hover:bg-accent transition-colors duration-200" style={{width: '12%'}}
                      onClick={() => handleSort("completed_at")}
                    >
                      Completed At{" "}
                      {sortBy === "completed_at" && (
                        <GoldArrow direction={sortDirection} />
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="bg-card">
                  {filteredTasks.map((task) => (
                    <TableRow
                      key={task.id}
                      className={`hover:bg-muted cursor-pointer transition-colors duration-200 ${task.status === "completed" ? "opacity-70" : ""}`}
                      onClick={(e) => {
                        if (
                          (e.target as HTMLElement).closest(
                            'button, input[type="checkbox"]'
                          )
                        )
                          return;
                        setSelectedTask(task);
                      }}
                    >
                      {inMergedScope && (
                        <TableCell className="truncate text-sm" style={{maxWidth: '0', width: '14%'}}>
                          <AccountCell label={accountLabelFor(task.tenant_id, accountNames)} />
                        </TableCell>
                      )}
                      <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (task.status === "completed") {
                              handleUncompleteTask(task.id);
                            } else {
                              handleCompleteTask(task.id);
                            }
                          }}
                          className="focus:outline-none group text-primary"
                          title={
                            task.status === "completed"
                              ? "Mark as incomplete"
                              : "Mark as complete"
                          }
                        >
                          {task.status === "completed" ? (
                            <span className="relative inline-block w-6 h-6 align-middle">
                              <Circle className="w-6 h-6 fill-primary stroke-primary" />
                              <Check
                                className="w-4 h-4 text-primary-foreground absolute left-1 top-1"
                                strokeWidth={3}
                              />
                            </span>
                          ) : (
                            <span className="relative inline-block w-6 h-6 align-middle">
                              <Circle className="w-6 h-6 transition-colors fill-transparent group-hover:fill-primary stroke-primary" />
                              <Check
                                className="w-4 h-4 text-primary-foreground absolute left-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                strokeWidth={3}
                              />
                            </span>
                          )}
                        </button>
                      </TableCell>
                      <TableCell
                        className={`font-medium text-sm text-foreground truncate ${task.status === "completed" ? "line-through" : ""}`}
                        title={task.title}
                        style={{maxWidth: '0', width: '30%'}}
                      >
                        {task.title}
                      </TableCell>
                      <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                        {task.project_id && projectNames[task.project_id] ? (
                          <div className="flex flex-col">
                            <span className="font-medium text-sm text-foreground">
                              {projectNames[task.project_id].name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {projectNames[task.project_id].company}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">
                            No project
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-foreground truncate">
                        {assignees[task.assignee_id ?? ""] || ""}
                      </TableCell>
                      <TableCell className="text-sm text-foreground truncate">
                        {task.due_date
                          ? formatRelativeDate(new Date(task.due_date))
                          : ""}
                      </TableCell>
                      <TableCell className="truncate" style={{maxWidth: '0', width: '25%'}}>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            task.priority === "high"
                              ? "bg-destructive/10 text-destructive"
                              : task.priority === "medium"
                                ? "bg-primary/10 text-primary"
                                : "bg-success/10 text-success"
                          }`}
                        >
                          {task.priority}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-foreground truncate">
                        {task.status === "completed" && task.completed_at
                          ? formatRelativeDate(new Date(task.completed_at))
                          : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTasks.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={inMergedScope ? 8 : 7}
                        className="text-center text-muted-foreground py-8"
                      >
                        {/* SUB-4.10 · D-6. NFR8's second sentence. */}
                        <ScopeAwareEmptyState scopeStatus={scopeStatus}>
                          No tasks found
                        </ScopeAwareEmptyState>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-50">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
        </div>
      )}
      <Dialog
        open={!!selectedTask}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseTaskModal();
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] task-modal-high-z">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Task" : "Task Details"}</DialogTitle>
          </DialogHeader>
          {selectedTask && !editing && (
            <TaskDetails
              task={selectedTask}
              onEdit={() => {
                setEditing(true);
              }}
              onDelete={async () => {
                await handleDeleteTask(selectedTask.id);
                handleCloseTaskModal();
              }}
              userId={userId}
              onComplete={handleCompleteTask}
              onUncomplete={handleUncompleteTask}
              onClose={() => handleCloseTaskModal()}
            />
          )}
          {selectedTask && editing && (
            <TaskModal
              isOpen={editing}
              onClose={() => setEditing(false)}
              onSubmit={async (updates) => {
                await handleUpdateTask(selectedTask.id, updates);
                handleCloseTaskModal();
              }}
              task={selectedTask}
            />
          )}
        </DialogContent>
      </Dialog>
      <TaskModal
        isOpen={showTaskForm}
        onClose={() => setShowTaskForm(false)}
        onSubmit={handleCreateTask}
        title="New Task"
        description="Create a new task"
      />
      <Dialog
        open={showAssignmentDialog}
        onOpenChange={setShowAssignmentDialog}
      >
        <DialogContent className="sm:max-w-[400px] task-modal-high-z">
          <DialogHeader>
            <DialogTitle>Task Assigned</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center">
            Task assigned to{" "}
            <span className="font-semibold">{assignmentDialogName}</span>.<br />
            <span className="text-xs text-muted-foreground">
              This will close in {assignmentDialogCountdown} second
              {assignmentDialogCountdown !== 1 ? "s" : ""}...
            </span>
          </div>
          <div className="flex justify-center">
            <Button onClick={() => setShowAssignmentDialog(false)} autoFocus>
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
